import { useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Info, Loader2, X } from "lucide-react";
import { Reveal, Eyebrow } from "../Reveal";
import { api, formatApiErrorDetail, getDevice, getUtm, getVisitorId, track } from "../../lib/api";
import { INTENTION_LANCEMENT, METIERS, NB_BOX_PRICE_OPTIONS, PRICE_QUESTIONS, REACTIONS_PRIX, REGIONS, VEHICULES_OPTIONS } from "../../lib/constants";

const inputCls = "w-full rounded-lg border border-[#2E333D] bg-[#0C0D0E] px-4 py-3.5 text-base text-[#F9FAFB] placeholder:text-gray-600 focus:border-[#FFC700] focus:outline-none transition-colors";
const labelCls = "block font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-2";

const Option = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`w-full text-left px-4 py-3.5 rounded-lg border text-sm font-medium transition-all ${active ? "border-[#FFC700] bg-[#FFC700]/10 text-[#FFC700]" : "border-[#2E333D] bg-[#0C0D0E] text-gray-300 hover:border-gray-500"}`}>
    {children}
  </button>
);

const EMPTY = {
  prix_trop_bas: "", prix_bonne_affaire: "", prix_cher_acceptable: "", prix_trop_cher: "",
  reaction_199: "", reaction_169: "", intention_lancement: "", email: "", nb_box: "",
  metier: "", region: "", vehicules: "", website: "",
};

export default function PriceStudy() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [consents, setConsents] = useState({ data: false, marketing: false });
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openSurvey = () => {
    track("click_price_interest", { target: "questionnaire_prix" });
    setOpen(true);
    setStep(1);
  };

  const close = () => {
    setOpen(false);
    if (done) {
      setDone(false);
      setForm(EMPTY);
      setConsents({ data: false, marketing: false });
      setStarted(false);
    }
  };

  const onFocus = () => {
    if (!started) {
      setStarted(true);
      track("form_start", { form: "prix" });
    }
  };

  const priceNum = (k) => parseFloat(String(form[k]).replace(",", "."));

  const validateStep = () => {
    if (step === 1) {
      const vals = PRICE_QUESTIONS.map((q) => priceNum(q.key));
      if (vals.some((v) => !Number.isFinite(v) || v <= 0)) {
        toast.error("Indiquez un prix en € TTC pour chaque question.");
        return false;
      }
      const [a, b, c, d] = vals;
      if (!(a <= b && b <= c && c <= d)) {
        toast.error("Vérifiez la cohérence : les 4 prix doivent être croissants (du plus bas au plus élevé).");
        return false;
      }
    }
    if (step === 2 && (!form.reaction_199 || !form.reaction_169)) {
      toast.error("Répondez aux deux scénarios de prix.");
      return false;
    }
    if (step === 3) {
      if (!form.intention_lancement) {
        toast.error("Sélectionnez une réponse.");
        return false;
      }
      if (form.intention_lancement !== "non" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        toast.error("Indiquez votre adresse e-mail pour être informé.");
        return false;
      }
      if (!form.nb_box) {
        toast.error("Indiquez le nombre de STOCKBOX envisagées.");
        return false;
      }
    }
    if (step === 4) {
      if (!form.metier || !form.region || !form.vehicules) {
        toast.error("Complétez votre métier, votre région et vos véhicules.");
        return false;
      }
      if (!consents.data) {
        toast.error("Votre consentement est requis pour envoyer vos réponses.");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(4, s + 1));
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      await api.post("/api/leads", {
        lead_type: "prix",
        email: form.intention_lancement !== "non" ? form.email.trim() : null,
        metier: form.metier,
        region: form.region,
        vehicules: form.vehicules,
        nb_box: form.nb_box,
        prix_trop_bas: priceNum("prix_trop_bas"),
        prix_bonne_affaire: priceNum("prix_bonne_affaire"),
        prix_cher_acceptable: priceNum("prix_cher_acceptable"),
        prix_trop_cher: priceNum("prix_trop_cher"),
        reaction_169: form.reaction_169,
        reaction_199: form.reaction_199,
        intention_lancement: form.intention_lancement,
        intention_achat: form.reaction_169 === "acheterait_probablement" || form.reaction_199 === "acheterait_probablement" ? "oui" : null,
        marketing_consent: consents.marketing,
        data_consent: consents.data,
        source: "etude_prix",
        visitor_id: getVisitorId(),
        device: getDevice(),
        website: form.website,
        ...getUtm(),
      });
      track("form_submit", { form: "prix" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const STEP_TITLES = {
    1: "Votre perception du prix",
    2: "Deux scénarios de lancement",
    3: "Votre intérêt",
    4: "Votre profil professionnel",
  };

  return (
    <section id="prix" className="py-16 sm:py-24" data-testid="price-study-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <Eyebrow>Étude de marché</Eyebrow>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-[#F9FAFB]" data-testid="price-study-title">
            Prix en cours d'étude
          </h2>
          <p className="mt-4 font-display text-xl sm:text-2xl font-semibold text-[#FFC700] uppercase tracking-wide">
            Quel serait selon vous le juste prix pour STOCKBOX 8 ?
          </p>
          <p className="mt-6 text-gray-400 leading-relaxed max-w-2xl mx-auto">
            STOCKBOX 8 est actuellement en développement. Nous souhaitons définir son prix
            de lancement avec les professionnels qui l'utiliseront réellement. Votre avis
            nous aidera à déterminer un prix cohérent avec la valeur du produit et les
            besoins du terrain.
          </p>
          <button onClick={openSurvey} data-testid="open-price-survey-button"
            className="mt-9 inline-flex items-center gap-2 bg-[#FFC700] text-[#0C0D0E] font-bold text-sm sm:text-base px-10 py-4 rounded-full uppercase tracking-wide hover:bg-[#FFD83D] hover:scale-[1.02] active:scale-[0.98] transition-all">
            Donner mon avis sur le prix <ArrowRight size={18} />
          </button>
          <p className="mt-5 flex items-center justify-center gap-2 text-xs text-gray-500">
            <Info size={14} className="text-gray-600" /> 2 minutes — aucune donnée utilisée sans votre consentement
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            data-testid="price-survey-modal"
            onClick={(e) => e.target === e.currentTarget && close()}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="w-full sm:max-w-lg bg-[#141619] border border-[#2E333D] rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
              onFocus={onFocus}
            >
              <div className="sticky top-0 bg-[#141619] border-b border-[#2E333D] px-5 sm:px-7 py-4 flex items-center justify-between z-10">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFC700]" data-testid="price-step-indicator">
                    {done ? "Terminé" : `Étape ${step} / 4`}
                  </p>
                  <p className="font-display font-bold uppercase text-[#F9FAFB] text-lg leading-tight">
                    {done ? "Merci !" : STEP_TITLES[step]}
                  </p>
                </div>
                <button onClick={close} data-testid="price-survey-close" className="p-2 text-gray-500 hover:text-[#FFC700] transition-colors" aria-label="Fermer">
                  <X size={20} />
                </button>
              </div>
              {!done && (
                <div className="h-1 bg-[#2E333D]">
                  <div className="h-full bg-[#FFC700] transition-all duration-300" style={{ width: `${(step / 4) * 100}%` }} />
                </div>
              )}

              <div className="px-5 sm:px-7 py-6">
                <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                {done ? (
                  <div className="text-center py-8" data-testid="price-survey-success">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-[#FFC700]/10 border border-[#FFC700]/40 flex items-center justify-center mb-6">
                      <CheckCircle2 size={28} className="text-[#FFC700]" />
                    </div>
                    <h3 className="font-display text-2xl font-bold uppercase text-[#F9FAFB]">Merci pour votre avis !</h3>
                    <p className="mt-4 text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
                      Vos réponses nous aident à définir un prix de lancement cohérent
                      avec la valeur réelle de STOCKBOX 8 pour les professionnels.
                    </p>
                    <button onClick={close} data-testid="price-survey-done-close"
                      className="mt-8 bg-[#FFC700] text-[#0C0D0E] font-bold text-sm px-8 py-3.5 rounded-full uppercase tracking-wide hover:bg-[#FFD83D] transition-colors">
                      Fermer
                    </button>
                  </div>
                ) : (
                  <>
                    {step === 1 && (
                      <div className="space-y-6" data-testid="price-step-1">
                        <p className="text-sm text-gray-400 leading-relaxed">
                          Aidez-nous à définir le prix de lancement de STOCKBOX 8.
                          Il n'y a pas de bonne ou de mauvaise réponse. Nous cherchons à connaître
                          votre perception réelle du prix pour un produit professionnel de ce type.
                        </p>
                        {PRICE_QUESTIONS.map((q, i) => (
                          <div key={q.key}>
                            <label className="block text-sm text-gray-300 font-medium mb-2 leading-snug" htmlFor={`price-${q.key}`}>
                              <span className="font-mono text-[#FFC700] mr-2">Q{i + 1}.</span>{q.label}
                            </label>
                            <div className="relative">
                              <input
                                id={`price-${q.key}`}
                                data-testid={`price-input-${q.key}`}
                                type="number" inputMode="numeric" min="1" step="1"
                                className={inputCls + " pr-16"}
                                placeholder="Ex : 150"
                                value={form[q.key]}
                                onChange={(e) => set(q.key, e.target.value)}
                              />
                              <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-xs text-gray-500">€ TTC</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-8" data-testid="price-step-2">
                        <p className="text-xs text-gray-500 leading-relaxed">
                          Les montants suivants sont uniquement des scénarios de recherche de marché —
                          ils ne représentent pas des prix définitifs.
                        </p>
                        <div>
                          <p className="text-sm text-gray-300 font-medium mb-3 leading-snug">
                            Si STOCKBOX 8 était proposée à <strong className="text-[#FFC700]">199 € TTC</strong> au lancement, quelle serait votre réaction ?
                          </p>
                          <div className="space-y-2.5">
                            {REACTIONS_PRIX.map((r) => (
                              <Option key={r.value} testid={`price-reaction-199-${r.value}`} active={form.reaction_199 === r.value} onClick={() => set("reaction_199", r.value)}>
                                {r.label}
                              </Option>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-300 font-medium mb-3 leading-snug">
                            Et si elle était proposée à <strong className="text-[#FFC700]">169 € TTC</strong> ?
                          </p>
                          <div className="space-y-2.5">
                            {REACTIONS_PRIX.map((r) => (
                              <Option key={r.value} testid={`price-reaction-169-${r.value}`} active={form.reaction_169 === r.value} onClick={() => set("reaction_169", r.value)}>
                                {r.label}
                              </Option>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-7" data-testid="price-step-3">
                        <div>
                          <p className="text-sm text-gray-300 font-medium mb-3 leading-snug">
                            Si STOCKBOX 8 correspond à vos besoins, souhaitez-vous être informé en priorité lors de son lancement ?
                          </p>
                          <div className="space-y-2.5">
                            {INTENTION_LANCEMENT.map((r) => (
                              <Option key={r.value} testid={`price-intention-${r.value}`} active={form.intention_lancement === r.value} onClick={() => set("intention_lancement", r.value)}>
                                {r.label}
                              </Option>
                            ))}
                          </div>
                        </div>
                        {form.intention_lancement && form.intention_lancement !== "non" && (
                          <div>
                            <label className={labelCls} htmlFor="price-email">Votre adresse e-mail *</label>
                            <input id="price-email" data-testid="price-email-input" type="email" className={inputCls}
                              placeholder="vous@entreprise.fr" value={form.email} onChange={(e) => set("email", e.target.value)} />
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-300 font-medium mb-3">Combien de STOCKBOX 8 pourriez-vous envisager pour votre activité ?</p>
                          <div className="flex flex-wrap gap-2.5">
                            {NB_BOX_PRICE_OPTIONS.map((o) => (
                              <button key={o} type="button" data-testid={`price-nbbox-${o.replace(/\s|\+/g, "-")}`}
                                onClick={() => set("nb_box", o)}
                                className={`px-5 py-3 rounded-lg border text-sm font-medium transition-all ${form.nb_box === o ? "border-[#FFC700] bg-[#FFC700]/10 text-[#FFC700]" : "border-[#2E333D] bg-[#0C0D0E] text-gray-300 hover:border-gray-500"}`}>
                                {o}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {step === 4 && (
                      <div className="space-y-6" data-testid="price-step-4">
                        <div>
                          <label className={labelCls} htmlFor="price-metier">Quel est votre métier ? *</label>
                          <select id="price-metier" data-testid="price-metier-select" className={inputCls} value={form.metier} onChange={(e) => set("metier", e.target.value)}>
                            <option value="">Sélectionnez votre métier</option>
                            {METIERS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls} htmlFor="price-region">Dans quelle région exercez-vous ? *</label>
                          <select id="price-region" data-testid="price-region-select" className={inputCls} value={form.region} onChange={(e) => set("region", e.target.value)}>
                            <option value="">Sélectionnez votre région</option>
                            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls} htmlFor="price-vehicules">Combien de véhicules utilitaires utilisez-vous dans votre activité ? *</label>
                          <select id="price-vehicules" data-testid="price-vehicules-select" className={inputCls} value={form.vehicules} onChange={(e) => set("vehicules", e.target.value)}>
                            <option value="">Sélectionnez</option>
                            {VEHICULES_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>
                        <div className="space-y-3 border-t border-[#2E333D] pt-5">
                          <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-300" data-testid="price-consent-data">
                            <input type="checkbox" checked={consents.data} onChange={(e) => setConsents((c) => ({ ...c, data: e.target.checked }))}
                              className="mt-0.5 w-4 h-4 accent-[#FFC700]" />
                            <span>J'accepte que mes réponses soient enregistrées et utilisées dans le cadre de l'étude de marché STOCKBOX 8. *</span>
                          </label>
                          <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-400" data-testid="price-consent-marketing">
                            <input type="checkbox" checked={consents.marketing} onChange={(e) => setConsents((c) => ({ ...c, marketing: e.target.checked }))}
                              className="mt-0.5 w-4 h-4 accent-[#FFC700]" />
                            <span>J'accepte de recevoir des communications marketing (facultatif).</span>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="mt-8 flex items-center gap-3">
                      {step > 1 && (
                        <button type="button" onClick={() => setStep((s) => s - 1)} data-testid="price-back-button"
                          className="inline-flex items-center gap-2 border border-[#2E333D] text-gray-300 font-semibold text-sm px-5 py-3.5 rounded-full hover:border-gray-500 transition-colors">
                          <ArrowLeft size={16} /> Retour
                        </button>
                      )}
                      {step < 4 ? (
                        <button type="button" onClick={next} data-testid="price-next-button"
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FFC700] text-[#0C0D0E] font-bold text-sm px-6 py-3.5 rounded-full uppercase tracking-wide hover:bg-[#FFD83D] transition-colors">
                          Continuer <ArrowRight size={16} />
                        </button>
                      ) : (
                        <button type="button" onClick={submit} disabled={submitting} data-testid="price-submit-button"
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FFC700] text-[#0C0D0E] font-bold text-sm px-6 py-3.5 rounded-full uppercase tracking-wide hover:bg-[#FFD83D] disabled:opacity-60 transition-colors">
                          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                          {submitting ? "Envoi..." : "Envoyer mes réponses"}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
